/* SemBot · autenticazione.
 *
 * Il backend firma un JWT e non ha refresh token: alla scadenza c'è solo il
 * rientro. Il contesto tiene la sessione, la persiste e registra sul client
 * HTTP come leggere il token e cosa fare al 401 — così nessun componente deve
 * ricordarsi di aggiungere l'intestazione.
 *
 * La risposta del server annida il token due volte
 * (`access_token.access_token`): la stranezza si assorbe qui e non esce.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { auth } from '../api/risorse';
import { collegaAutenticazione, ErroreApi } from '../api/client';

const CHIAVE_ARCHIVIO = 'sembot.sessione';

interface Sessione {
  token: string;
  utente: { id: number; username: string };
}

interface ValoreContesto {
  sessione: Sessione | null;
  autenticato: boolean;
  inCorso: boolean;
  errore: string | null;
  accedi: (username: string, password: string) => Promise<boolean>;
  esci: () => void;
}

const Contesto = createContext<ValoreContesto | null>(null);

function leggiArchivio(): Sessione | null {
  try {
    const grezzo = localStorage.getItem(CHIAVE_ARCHIVIO);
    if (!grezzo) return null;
    const s = JSON.parse(grezzo) as Sessione;
    return typeof s?.token === 'string' && s.token ? s : null;
  } catch {
    return null;
  }
}

/** Il token è già scaduto? Si legge da `exp`, senza verificarne la firma:
 *  serve solo a non mostrare una schermata che il server rifiuterà comunque. */
function scaduto(token: string): boolean {
  try {
    const carico = JSON.parse(atob(token.split('.')[1])) as { exp?: number };
    return typeof carico.exp === 'number' && carico.exp * 1000 <= Date.now();
  } catch {
    return false;
  }
}

export function FornitoreAccesso({ children }: { children: ReactNode }) {
  const [sessione, setSessione] = useState<Sessione | null>(() => {
    const s = leggiArchivio();
    return s && !scaduto(s.token) ? s : null;
  });
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const esci = useCallback(() => {
    localStorage.removeItem(CHIAVE_ARCHIVIO);
    setSessione(null);
  }, []);

  /* Il collegamento al client HTTP avviene DURANTE IL RENDER, non in un effetto, e legge la
     sessione da un riferimento invece che da una chiusura.
     
     Con un effetto si creava un giro chiuso da cui non si usciva piu': in React gli effetti dei
     componenti figli vengono eseguiti PRIMA di quelli del genitore, quindi le pagine protette
     lanciavano le loro richieste mentre il lettore del token era ancora quello iniziale, che
     torna sempre null. La prima richiesta partiva senza intestazione, il server rispondeva 401,
     il client trattava il 401 come sessione scaduta e riportava alla pagina di accesso -- che
     rimontava le stesse pagine, che rifacevano le stesse richieste.
     
     Il riferimento risolve anche cio' per cui l'effetto era stato scritto: la funzione
     registrata legge sempre il valore corrente, quindi si registra una volta sola e continua a
     valere dopo un rientro, senza chiusure sul valore vecchio. */
  const sessioneCorrente = useRef(sessione);
  sessioneCorrente.current = sessione;
  const esciRef = useRef(esci);
  esciRef.current = esci;
  const collegato = useRef(false);
  if (!collegato.current) {
    collegato.current = true;
    collegaAutenticazione(
      () => sessioneCorrente.current?.token ?? null,
      () => esciRef.current(),
    );
  }

  const accedi = useCallback(async (username: string, password: string) => {
    setInCorso(true);
    setErrore(null);
    try {
      const risposta = await auth.accedi({ username, password });
      const token = risposta?.access_token?.access_token;
      if (!token) {
        setErrore('Il server non ha restituito un token di accesso.');
        return false;
      }
      const nuova: Sessione = { token, utente: risposta.user };
      localStorage.setItem(CHIAVE_ARCHIVIO, JSON.stringify(nuova));
      setSessione(nuova);
      return true;
    } catch (e) {
      setErrore(
        e instanceof ErroreApi && e.stato === 401
          ? 'Nome utente o password non validi.'
          : e instanceof Error
            ? e.message
            : 'Accesso non riuscito.',
      );
      return false;
    } finally {
      setInCorso(false);
    }
  }, []);

  const valore = useMemo<ValoreContesto>(
    () => ({
      sessione,
      autenticato: sessione !== null,
      inCorso,
      errore,
      accedi,
      esci,
    }),
    [sessione, inCorso, errore, accedi, esci],
  );

  return <Contesto.Provider value={valore}>{children}</Contesto.Provider>;
}

export function usaAccesso(): ValoreContesto {
  const c = useContext(Contesto);
  if (!c) throw new Error('usaAccesso richiede FornitoreAccesso a monte.');
  return c;
}
